/**
 * react-i18next setup.
 *
 * Bevat de gebundelde defaults (en/nl/de/fr). EN is de canonieke key-set
 * (ADR-0010), NL de primaire auteurstaal; DE (Sie-vorm) en FR (vouvoiement)
 * zijn volledig vertaald (issue #88). Eventuele ontbrekende keys vallen via
 * fallbackLng terug op EN.
 *
 * `i18nDefaults` is een immutable deep-clone-snapshot zodat per-tenant
 * overrides (ADR-0021) bij delete/reset terug kunnen vallen op de
 * canonieke platform-vertaling zonder reload.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { bundles, SPA_LOCALES } from './bundles';

// De bundels en de lijst met talen staan in ./bundles.ts, zonder bijwerking. Wie
// alleen de lijst nodig heeft importeert dáár; deze module start i18next.
export { i18nDefaults, SPA_LOCALES, type SpaLocale } from './bundles';

// De taalkiezer (AppShell) persist de keuze in localStorage('locale'); die
// wint bij een volgende load van de browser-taal. Zonder deze read viel de
// UI-taal bij elke reload terug op de browser-taal.
const persisted = ((): string | null => {
  try {
    return window.localStorage.getItem('locale');
  } catch {
    return null; // private browsing — geen persistentie, wel gewoon werken
  }
})();

i18n.use(initReactI18next).init({
  resources: Object.fromEntries(
    Object.entries(bundles).map(([code, bundle]) => [code, { translation: bundle }]),
  ),
  // Initiële taal: bewaarde keuze → browser-taal → Engels. Na login kan de
  // app alsnog wisselen (bv. tenant-locale-restrictie in App.tsx).
  lng:
    SPA_LOCALES.find((l) => l === persisted) ??
    SPA_LOCALES.find((l) => l === navigator.language?.slice(0, 2)) ??
    'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false, // React escapet zelf al
    // Single-brace delimiter — onze locale-JSON's gebruiken `{name}`
    // i.p.v. i18next's default `{{name}}` zodat ze 1:1 compatible
    // blijven met de Vue-versie tijdens de migratie. Belangrijk: keys
    // die letterlijk `{` of `}` zonder placeholder bevatten zijn rare
    // edge-cases — die hebben we niet, dus geen escaping nodig.
    prefix: '{',
    suffix: '}',
  },
});

/**
 * `<html lang>` meeschrijven met de actieve taal — issue 627.
 *
 * ⚠️ DIT STOND NERGENS, EN HET WAS DE STANDAARDSITUATIE. `index.html` heeft
 * `<html lang="en">` hardgecodeerd en niets werkte dat ooit bij. De meeste
 * omgevingen staan op `nl`, dus de pagina meldde zich altijd al als Engels — ook
 * nadat de hele navigatie Duits was geworden.
 *
 * WCAG 2.2 SC 3.1.1 is een A-criterium, en het is geen formaliteit: een
 * schermlezer kiest zijn uitspraakregels op dit attribuut. Nederlandse tekst
 * aangekondigd als Engels wordt letterlijk onverstaanbaar voorgelezen. Het raakt
 * ook spellingcontrole in formuliervelden, automatische vertaling en afbreking.
 *
 * Op het `languageChanged`-event en niet op één plek bij het opstarten: de app
 * wisselt ook ná login (de tenant-locale-restrictie in `App.tsx`), en die tweede
 * weg zou anders stil de oude waarde laten staan.
 */
function syncDocumentLanguage(language: string): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.lang = language;
}

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export { i18n };
